/**
 * DesktopNav — the customer navigation from 1024px up: a top bar with the tenant's brand and the same five
 * destinations as BottomNav (which is hidden at that width). Rendered by the customer shell in App.js, above the
 * content column, on every customer route except the login / onboarding / legal screens. It is static (not sticky)
 * so each theme's own sticky page header keeps working exactly as before underneath it. Layout only: the tabs,
 * routes and active rule come from BottomNav.
 */
import { Link, useLocation } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import { t } from "../lib/i18n";
import { CUSTOMER_TABS, isCustomerTabActive } from "./BottomNav";

export default function DesktopNav() {
    const { pathname } = useLocation();
    const { tenant, lang } = useTenant();
    const branding = tenant?.branding || {};
    const logo = branding.customer_logo_url || branding.logo_url || null;
    const name = branding.app_name || tenant?.name || "";
    return (
        <header data-testid="desktop-nav" className="hidden lg:block bg-card border-b border-border">
            <div className="mx-auto flex items-center gap-6 px-6 h-14" style={{ maxWidth: "var(--shell-width)" }}>
                <Link to="/" data-testid="desktop-nav-brand" className="flex items-center gap-2.5 min-w-0 shrink-0">
                    {logo && <img src={logo} alt="" className="h-8 w-8 rounded-lg object-contain" />}
                    <span className="font-display font-bold text-base truncate max-w-[220px]">{name}</span>
                </Link>
                <nav aria-label="Main" className="ml-auto flex items-center gap-1">
                    {CUSTOMER_TABS.map(({ to, icon: Icon, key }) => {
                        const active = isCustomerTabActive(pathname, to);
                        return (
                            <Link key={to} to={to} data-testid={`desktop-nav-${key}`} aria-current={active ? "page" : undefined}
                                  className={`inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm transition ${active ? "text-primary font-semibold bg-primary/[0.08]" : "text-muted-foreground font-medium hover:bg-muted hover:text-foreground"}`}>
                                <Icon size={18} strokeWidth={active ? 2.5 : 2} />{t(key, lang)}
                            </Link>
                        );
                    })}
                </nav>
            </div>
        </header>
    );
}
